import { Component, OnInit, Inject } from '@angular/core';
import { FormGroup, Validators, FormBuilder } from '@angular/forms';
import { RenameFileDialogData, MediaFile, ConfirmDialogData, FileNode, MediaTranslation } from 'src/app/utils/common-classes';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material';
import { FileService } from 'src/app/services/File/file.service';
import { UtilsService } from 'src/app/services/Utils/utils.service';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'app-rename-file-dialog',
  templateUrl: './rename-file-dialog.component.html',
  styleUrls: ['./rename-file-dialog.component.scss']
})
export class RenameFileDialogComponent implements OnInit {

  public file: FileNode;
  public form: FormGroup;
  public renameFileError: string;
  public formSubmitAttempt: boolean;
  public extension: string;
  public selectedLanguage: string;
  private selectedLanguageName: string;
  private selectedLanguageDescription: string;
  public creatingNew: boolean;

  constructor(
    public dialogRef: MatDialogRef<RenameFileDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: RenameFileDialogData,
    private fb: FormBuilder,
    private fileService: FileService,
    public utils: UtilsService,
    private dialog: MatDialog) { }

  ngOnInit() {
    this.form = this.fb.group({
      filename: ['', [Validators.required, Validators.pattern('^[^\\/?%*:|"<>\]+$')]],
      description: ['']
    });
    this.file = this.data.file;
    this.form.get('filename').setValue(this.file.name.substring(0, this.file.name.lastIndexOf('.')));
    this.form.get('description').setValue(this.file.description);
    this.selectedLanguageName = this.form.get('filename').value;
    this.selectedLanguageDescription = this.form.get('description').value;
    this.extension = this.file.name.substring(this.file.name.lastIndexOf('.'), this.file.name.length);
    this.utils.setDialogUnclosable(true);
  }

  public async closeDialog() {
    let _self = this;
    if (!_self.edited() && !_self.creatingNew) {
      _self.utils.setDialogUnclosable(false);
      _self.dialogRef.close(_self.file);
    } else {
      let title: string = await _self.utils.translateKey("dialog.confirm.title.unsaved-changes");
      let message: string = await _self.utils.translateKey("dialog.confirm.message.unsaved-changes");
      let data: ConfirmDialogData = { title: title, message: message };
      let confirmLeaveDialog: MatDialogRef<ConfirmDialogComponent> = _self.dialog.open(ConfirmDialogComponent, { width: "400px", data: data, disableClose: true });
      confirmLeaveDialog.afterClosed().subscribe((confirm: boolean) => {
        if (confirm) {
          _self.utils.setDialogUnclosable(false);
          _self.dialogRef.close(_self.file);
        }
      });
    }
  }

  public submit(): void {
    let _self = this;
    _self.formSubmitAttempt = true;
    if (_self.form.valid) {
      let filename = _self.form.get('filename').value + _self.extension;
      let description = _self.form.get('description').value;
      _self.utils.showShortLoader();
      let mediaTranslation: MediaTranslation = new MediaTranslation();
      mediaTranslation.languageId = _self.selectedLanguage;
      mediaTranslation.name = filename;
      mediaTranslation.description = description;
      mediaTranslation.mediaFileId = _self.file.mediaFileId;
      _self.fileService.setFileTranslation(mediaTranslation).then((file: MediaFile) => {
        _self.file = FileNode.fromMediaFile(file);
        _self.updateData(_self.selectedLanguage);
        _self.creatingNew = false;
      }).catch((err) => {
        return _self.utils.getError(err);
      }).finally(() => {
        _self.utils.hideShortLoader();
      });
    }
  }

  public async deleteTranslation() {
    let _self = this;
    if (!_self.creatingNew && _self.file.translations && _self.file.translations.get(_self.selectedLanguage)) {
      let title: string = await _self.utils.translateKey("dialog.confirm.title.delete-translation");
      let message: string = await _self.utils.translateKey("dialog.confirm.message.delete-translation");
      let data: ConfirmDialogData = { title: title, message: message };
      let confirmLeaveDialog: MatDialogRef<ConfirmDialogComponent> = _self.dialog.open(ConfirmDialogComponent, { width: "400px", data: data, disableClose: true });
      confirmLeaveDialog.afterClosed().subscribe((confirm: boolean) => {
        if (confirm) {
          let mediaTranslation: MediaTranslation = new MediaTranslation();
          mediaTranslation.mediaTranslationId = _self.file.translations.get(_self.selectedLanguage).mediaTranslationId;
          _self.fileService.deleteTranslation(mediaTranslation).then((file: MediaFile) => {
            _self.file = FileNode.fromMediaFile(file);
            _self.updateData(_self.selectedLanguage);
            _self.creatingNew = false;
          }).catch((err) => {
            return _self.utils.getError(err);
          }).finally(() => {
            _self.utils.hideShortLoader();
          });
        }
      });
    } else {
      _self.updateData(_self.selectedLanguage);
      _self.creatingNew = false;
    }
  }

  public cancel(): void {
    let _self = this;
    _self.updateData(_self.selectedLanguage);
  }

  public async selectLanguage(language: string) {
    let _self = this;
    if (language != _self.selectedLanguage) {
      if (!_self.edited() && !_self.creatingNew) {
        _self.updateData(language);
      } else {
        let title: string = await _self.utils.translateKey("dialog.confirm.title.unsaved-changes");
        let message: string = await _self.utils.translateKey("dialog.confirm.message.unsaved-changes");
        let data: ConfirmDialogData = { title: title, message: message };
        let confirmLeaveDialog: MatDialogRef<ConfirmDialogComponent> = _self.dialog.open(ConfirmDialogComponent, { width: "400px", data: data, disableClose: true });
        confirmLeaveDialog.afterClosed().subscribe((cancel: boolean) => {
          if (cancel) {
            _self.updateData(language);
            _self.creatingNew = false;
          }
        });
      }
    }
  }

  private updateData(language: string): void {
    let _self = this;
    let file: FileNode = _self.file;
    _self.selectedLanguageName = "";
    _self.selectedLanguageDescription = "";
    if (language) {
      if (file.translations.get(language)) {
        if (_self.file.translations.get(language).name && _self.file.translations.get(language).name.lastIndexOf('.') > -1) {
          _self.selectedLanguageName = _self.file.translations.get(language).name.substring(0, _self.file.translations.get(language).name.lastIndexOf('.'));
        } else {
          _self.selectedLanguageName = _self.file.translations.get(language).name;
        }
        _self.selectedLanguageDescription = _self.file.translations.get(language).description || "";
      }
    } else {
      _self.selectedLanguageName = _self.file.name.substring(0, _self.file.name.lastIndexOf('.'));
      _self.selectedLanguageDescription = _self.file.description || "";
    }

    _self.form.get('filename').setValue(_self.selectedLanguageName);
    _self.form.get('description').setValue(_self.selectedLanguageDescription);
    _self.selectedLanguage = language;
  }

  public edited(): boolean {
    let _self = this;
    return _self.selectedLanguageName != _self.form.get('filename').value || _self.selectedLanguageDescription != _self.form.get('description').value;
  }

  public createNewTranslation(): void {
    let _self = this;
    _self.creatingNew = true;
  }

}